// LeftPanel.js - Component Palette

import React, { useState } from 'react';
import styled from 'styled-components';
import { useReactFlow, MarkerType } from '@xyflow/react';
import { COMPONENT_LIBRARY, COMPONENT_CATEGORIES } from '../../data/componentLibrary';
import { createComponentNode } from '../../utils/componentNodeFactory';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';
import CatalogComponentModal from '../Modals/CatalogComponentModal';
import S2PUploadModal from '../Modals/S2PUploadModal';

// RF Component Symbol Watermarks (SVG paths) - imported from RFNode
const COMPONENT_SYMBOLS = {
  // Sources: Sine wave
  inputSource: 'M8,20 Q12,10 16,20 T24,20 T32,20',
  noiseSource: 'M8,20 Q12,10 16,20 T24,20 T32,20',

  // Amplifiers: Triangle
  lna: 'M10,10 L30,20 L10,30 Z',
  powerAmplifier: 'M10,10 L30,20 L10,30 Z',
  vga: 'M10,10 L30,20 L10,30 Z',
  gainBlock: 'M10,10 L30,20 L10,30 Z',
  driver: 'M10,10 L30,20 L10,30 Z',

  // Attenuators: Zigzag resistor pattern
  attenuator: 'M10,20 L13,15 L16,25 L19,15 L22,25 L25,15 L28,20 L30,20',
  variableAttenuator: 'M10,20 L13,15 L16,25 L19,15 L22,25 L25,15 L28,20 L30,20 M12,28 L28,12',

  // Cable: Simple horizontal line
  cable: 'M10,20 L30,20',

  // Splitter/Combiner: Y-shape
  splitter: 'M10,20 L20,20 M20,15 L25,12 M20,25 L25,28',
  combiner: 'M30,20 L20,20 M20,15 L15,12 M20,25 L15,28',

  // Filters: Frequency response curves
  bandpassFilter: 'M10,25 L13,25 L16,10 L19,10 L22,25 L25,25',
  lowpassFilter: 'M10,10 L15,10 Q20,15 25,25',
  highpassFilter: 'M10,25 Q15,15 20,10 L25,10',
  bandstopFilter: 'M10,15 L13,15 L16,25 L19,25 L22,15 L25,15',

  // Mixers: X symbol
  passiveMixer: 'M10,10 L30,30 M30,10 L10,30 M15,20 L25,20',
  activeMixer: 'M10,10 L30,30 M30,10 L10,30 M15,20 L25,20',

  // Switches: Contact points
  spdtSwitch: 'M10,20 L18,20 L25,15 M25,25 L24,24',
  sp4tSwitch: 'M10,20 L18,20 M22,12 L21,13 M28,15 L27,16 M28,25 L27,24 M22,28 L21,27',

  // Termination: Ground symbol
  termination: 'M20,15 L20,25 M15,25 L25,25 M16,27 L24,27 M18,29 L22,29',

  // Generic: Circle
  generic: 'M20,20 m-8,0 a8,8 0 1,0 16,0 a8,8 0 1,0 -16,0',

  // Note: Document icon
  note: 'M12,10 L25,10 L28,13 L28,30 L12,30 Z M25,10 L25,13 L28,13'
};

// Helper function to find terminal measurement node connected to a given node
const findConnectedTerminalMeasurement = (sourceNode, nodes, edges) => {
  // Find all measurement nodes
  const measurementNodes = nodes.filter(node => node.type === 'measurement');

  // Filter to terminal measurements (have no outgoing connections)
  const terminalMeasurements = measurementNodes.filter(measNode =>
    !edges.some(edge => edge.source === measNode.id)
  );

  // Find the terminal measurement connected to the source node
  return terminalMeasurements.find(measNode => {
    return edges.some(edge =>
      edge.source === sourceNode.id && edge.target === measNode.id
    );
  });
};

const LeftPanel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['sources', 'amplifiers', 'passive']));
  const [componentFilter, setComponentFilter] = useState('all'); // 'all', 'generic', 'catalog'
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // For right-click menu
  const [showFavorites, setShowFavorites] = useState(true); // Show favorites section
  const [tooltip, setTooltip] = useState(null); // { comp, x, y }
  const [duplicateSource, setDuplicateSource] = useState(null); // For duplicate mode
  const [editingComponent, setEditingComponent] = useState(null); // For edit mode
  const [s2pUploadModalOpen, setS2pUploadModalOpen] = useState(false); // S2P upload modal
  const [s2pUploadComponent, setS2pUploadComponent] = useState(null); // Component for S2P upload
  const { nodes, edges, setNodes, setEdges, addNode, componentLibrary, catalogComponents, toggleFavorite, chainMode, toggleChainMode, loadComponentLibrary, showTutorial, tutorialStep, completeTutorialAction } = useStore();
  const categoryListRef = React.useRef(null);
  const { getViewport } = useReactFlow();

  // Grid snapping helper
  const GRID_SIZE = 15;
  const snapToGrid = (position) => ({
    x: Math.round(position.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(position.y / GRID_SIZE) * GRID_SIZE
  });

  // Close context menu on click away
  React.useEffect(() => {
    const handleClickAway = () => {
      if (contextMenu) {
        handleCloseContextMenu();
      }
    };
    document.addEventListener('click', handleClickAway);
    return () => document.removeEventListener('click', handleClickAway);
  }, [contextMenu]);

  // Set filter to 'generic' and scroll to top when tutorial starts
  React.useEffect(() => {
    if (showTutorial) {
      setComponentFilter('generic');
      // Scroll component list to top
      if (categoryListRef.current) {
        categoryListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [showTutorial]);

  // Tutorial step 5 (index 5 = add-measurement): expand measurement category first, then scroll
  React.useEffect(() => {
    if (showTutorial && tutorialStep === 5) {
      // First expand the measurement category
      setExpandedCategories(prev => {
        const newSet = new Set(prev);
        newSet.add('measurement');
        return newSet;
      });
      // After expansion renders, scroll down to make Measurement visible
      setTimeout(() => {
        if (categoryListRef.current) {
          categoryListRef.current.scrollTo({ top: categoryListRef.current.scrollHeight, behavior: 'smooth' });
        }
      }, 100);
    }
    // Tutorial step 9 (index 9 = add-lna): scroll back to top so LNA is visible
    if (showTutorial && tutorialStep === 9) {
      if (categoryListRef.current) {
        categoryListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    // Tutorial step 14 (index 14 = palette-tooltips): switch to All Components and scroll to top
    if (showTutorial && tutorialStep === 14) {
      setComponentFilter('all');
      if (categoryListRef.current) {
        categoryListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [showTutorial, tutorialStep]);

  // Handle chain mode toggle with tutorial tracking
  const handleChainModeToggle = () => {
    toggleChainMode();
    // Track enabling chain mode for tutorial (not disabling)
    if (showTutorial && !chainMode) {
      completeTutorialAction('chainModeEnabled');
    }
  };

  const handleDragStart = (event, componentType) => {
    event.dataTransfer.setData('application/reactflow', componentType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleAddComponent = (componentType) => {
    console.log('handleAddComponent called with:', componentType);
    // Use componentLibrary from store (includes both generic and catalog)
    const componentDef = componentLibrary[componentType] || COMPONENT_LIBRARY[componentType];
    console.log('componentDef:', componentDef);
    if (!componentDef) {
      console.error('No component definition found for:', componentType);
      return;
    }

    // Manual Mode (chainMode OFF) OR Note component: Place at top-left with cascade offset
    // Notes always use manual positioning regardless of chain mode
    if (!chainMode || componentType === 'note') {
      // Get viewport to convert screen to flow coordinates
      const { x: vpX, y: vpY, zoom } = getViewport();

      // Calculate 50px from top-left of viewport in flow coordinates
      const startX = (-vpX + 50) / zoom;
      const startY = (-vpY + 50) / zoom;

      // Start at top-left, then offset until we find an unoccupied spot
      const cascadeStep = 30;
      let offsetMultiplier = 0;
      let position;

      // Keep offsetting until no node is within 20px of the target position
      while (offsetMultiplier < 50) { // Safety limit
        const candidateX = startX + (offsetMultiplier * cascadeStep);
        const candidateY = startY + (offsetMultiplier * cascadeStep);

        const isOccupied = nodes.some(node => {
          const dx = Math.abs(node.position.x - candidateX);
          const dy = Math.abs(node.position.y - candidateY);
          return dx < 20 && dy < 20;
        });

        if (!isOccupied) {
          position = snapToGrid({ x: candidateX, y: candidateY });
          break;
        }
        offsetMultiplier++;
      }

      // Fallback if all positions occupied (shouldn't happen)
      if (!position) {
        position = snapToGrid({
          x: startX + (offsetMultiplier * cascadeStep),
          y: startY + (offsetMultiplier * cascadeStep)
        });
      }

      // Use factory to create node (ensures proper catalog component initialization)
      const newNode = createComponentNode(componentDef, {
        position,
        nodeType: componentType
      });

      if (!newNode) {
        console.error('Failed to create node in manual mode:', componentType);
        return;
      }

      // Use addNode to trigger async frequency data loading for catalog components
      addNode(newNode);
      console.log('Component added in manual mode:', newNode.id);
      return;
    }

    // Chain Mode (ON): Smart positioning and auto-connect logic
    // Calculate position: align new component's input with first unused output of rightmost node
    let position;
    let connectedMeasurement = null;  // Track if we need to insert before measurement
    let measurementEdge = null;

    if (nodes.length === 0) {
      // First node: place at default position
      position = snapToGrid({ x: 250, y: 150 });
    } else {
      // Find the rightmost node (excluding measurement nodes to avoid adding after them)
      const nonMeasurementNodes = nodes.filter(node => node.type !== 'measurement');
      const rightmostNode = (nonMeasurementNodes.length > 0 ? nonMeasurementNodes : nodes).reduce((rightmost, node) =>
        node.position.x > rightmost.position.x ? node : rightmost
      );

      // Get the store's edges to check which outputs are used
      const { edges, componentLibrary: storeLibrary } = useStore.getState();
      const rightmostNodeDef = storeLibrary[rightmostNode.type] || COMPONENT_LIBRARY[rightmostNode.type];

      // Check if we should insert before a terminal measurement node
      // Only do this if the new component is NOT a measurement itself
      if (componentType !== 'measurement') {
        connectedMeasurement = findConnectedTerminalMeasurement(rightmostNode, nodes, edges);
        if (connectedMeasurement) {
          measurementEdge = edges.find(edge =>
            edge.source === rightmostNode.id && edge.target === connectedMeasurement.id
          );
        }
      }

      // Find the first unused output handle of the rightmost node
      let targetOutputIndex = 0;
      if (rightmostNodeDef?.outputs && rightmostNodeDef.outputs.length > 0) {
        // Check which outputs are already connected
        const usedOutputs = edges
          .filter(edge => edge.source === rightmostNode.id)
          .map(edge => edge.sourceHandle);

        // Find first unused output
        const unusedOutput = rightmostNodeDef.outputs.find(output => !usedOutputs.includes(output));
        if (unusedOutput) {
          targetOutputIndex = rightmostNodeDef.outputs.indexOf(unusedOutput);
        }
      }

      // Calculate Y offset based on handle positions
      // Handles are positioned at 50% + idx * 20% from top of node
      const outputHandleOffset = (50 + targetOutputIndex * 20) / 100;

      // New component's first input should align with the output
      const newComponentInputIndex = 0; // First input
      const inputHandleOffset = (50 + newComponentInputIndex * 20) / 100;

      // Query actual node dimensions from DOM
      const rightmostNodeElement = document.querySelector(`[data-id="${rightmostNode.id}"]`);
      let nodeWidth = 220; // fallback
      let nodeHeight = 150; // fallback

      if (rightmostNodeElement) {
        const rect = rightmostNodeElement.getBoundingClientRect();
        const viewport = getViewport();
        nodeWidth = rect.width / viewport.zoom;
        nodeHeight = rect.height / viewport.zoom;
      }

      const yAdjustment = (outputHandleOffset - inputHandleOffset) * nodeHeight;

      // If inserting before measurement, calculate position between rightmost and measurement
      if (connectedMeasurement && measurementEdge) {
        // Position new node at measurement's current position
        // (measurement will be moved later)
        position = snapToGrid({
          x: connectedMeasurement.position.x,
          y: rightmostNode.position.y + yAdjustment
        });
      } else {
        // Normal case: place new node to the right
        position = snapToGrid({
          x: rightmostNode.position.x + nodeWidth + 30,
          y: rightmostNode.position.y + yAdjustment
        });
      }
    }

    // Use factory to create node (ensures proper catalog component initialization)
    const newNode = createComponentNode(componentDef, {
      position,
      nodeType: componentType
    });

    if (!newNode) {
      console.error('Failed to create node in chain mode:', componentType);
      return;
    }

    console.log('Creating new node:', newNode);
    console.log('Current nodes before:', nodes);

    // Add node - recalculation is already deferred in the store
    // If inserting before measurement, also move measurement in same update
    let updatedNodes;
    if (connectedMeasurement && measurementEdge) {
      // Move measurement node immediately
      const updatedMeasurement = {
        ...connectedMeasurement,
        position: {
          x: position.x + 190 + 30, // Node width + spacing
          y: connectedMeasurement.position.y
        }
      };
      // Update existing nodes (measurement position change)
      updatedNodes = nodes.map(node =>
        node.id === connectedMeasurement.id ? updatedMeasurement : node
      );
      setNodes(updatedNodes);
      console.log('Moved measurement synchronously to:', updatedMeasurement.position);
    }

    // Use addNode for new node to trigger async frequency data loading for catalog components
    console.log('About to call addNode with:', newNode);
    addNode(newNode);

    console.log('addNode called');

    // Automatically connect if there's a rightmost node with unused outputs and new node has inputs
    if (nodes.length > 0 && componentDef.inputs && componentDef.inputs.length > 0) {
      // Find the rightmost node again (excluding measurement nodes)
      const nonMeasurementNodes = nodes.filter(node => node.type !== 'measurement');
      const rightmostNode = (nonMeasurementNodes.length > 0 ? nonMeasurementNodes : nodes).reduce((rightmost, node) =>
        node.position.x > rightmost.position.x ? node : rightmost
      );

      const { edges: currentEdges } = useStore.getState();
      // For catalog components, use baseType to find definition; for generic, use type directly
      const rightmostNodeDef = rightmostNode.data?.baseType
        ? COMPONENT_LIBRARY[rightmostNode.data.baseType]
        : COMPONENT_LIBRARY[rightmostNode.type];

      // Find the first unused output handle of the rightmost node
      if (rightmostNodeDef?.outputs && rightmostNodeDef.outputs.length > 0) {
        const usedOutputs = currentEdges
          .filter(edge => edge.source === rightmostNode.id &&
                          !(measurementEdge && edge.id === measurementEdge.id))
          .map(edge => edge.sourceHandle);

        const unusedOutput = rightmostNodeDef.outputs.find(output => !usedOutputs.includes(output));

        // If there's an unused output, create connections
        if (unusedOutput) {
          // Check if we're inserting before a measurement node
          if (connectedMeasurement && measurementEdge) {
            // Validate that the new component has outputs before creating measurement insertion edges
            if (!componentDef.outputs || componentDef.outputs.length === 0) {
              console.warn('Cannot insert before measurement: component has no outputs', componentType);
              // Skip measurement insertion logic - component can't connect to measurement without outputs
            } else {
              // Create two edges: rightmost -> new -> measurement
              const edgeToNew = {
                id: `edge-${Date.now()}-1`,
                source: rightmostNode.id,
                sourceHandle: unusedOutput,
                target: newNode.id,
                targetHandle: componentDef.inputs[0],
                type: 'default',
                animated: false,
                style: { stroke: THEME.connectionLine, strokeWidth: 2 }
              };

              const edgeToMeasurement = {
                id: `edge-${Date.now()}-2`,
                source: newNode.id,
                sourceHandle: componentDef.outputs[0],
                target: connectedMeasurement.id,
                targetHandle: 'rfIn',
                type: 'default',
                animated: false,
                style: { stroke: THEME.connectionLine, strokeWidth: 2 }
              };

              // Remove old edge from rightmost to measurement
              const updatedEdges = currentEdges.filter(e => e.id !== measurementEdge.id);
              setEdges([...updatedEdges, edgeToNew, edgeToMeasurement]);
              console.log('Auto-connected with measurement insertion:', edgeToNew, edgeToMeasurement);
              // Note: Measurement node was already moved synchronously above
            }
          } else {
            // Normal case: just connect rightmost to new node
            const newEdge = {
              id: `edge-${Date.now()}`,
              source: rightmostNode.id,
              sourceHandle: unusedOutput,
              target: newNode.id,
              targetHandle: componentDef.inputs[0],
              type: 'default',
              animated: false,
              style: { stroke: THEME.connectionLine, strokeWidth: 2 }
            };

            const updatedEdges = [...currentEdges, newEdge];
            setEdges(updatedEdges);
            console.log('Auto-connected:', newEdge);
          }
        }
      }
    }

    // Dispatch event to trigger fitView from MainLayout after React has processed the state update
    setTimeout(() => {
      const currentNodes = useStore.getState().nodes;
      console.log('Nodes after setNodes:', currentNodes);

      // Trigger fitView after nodes are in the store
      window.dispatchEvent(new CustomEvent('fit-view-requested'));
    }, 100);

  };

  const openCatalogModal = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setIsCatalogModalOpen(true);
  };

  const handleContextMenu = (event, comp) => {
    event.preventDefault();
    setTooltip(null); // Clear tooltip when opening context menu
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      component: comp
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleViewDatasheet = () => {
    if (!contextMenu?.component) return;

    const partNumber = contextMenu.component.partNumber;
    const sku = partNumber.replace(/^GRF/, ''); // Remove "GRF" prefix
    const url = `https://www.guerrilla-rf.com/products/DataSheet?sku=${sku}&file_name=${partNumber}DS.pdf`;
    window.open(url, '_blank');
    handleCloseContextMenu();
  };

  const handleDeleteCatalogComponent = async () => {
    if (!contextMenu?.component) return;

    const comp = contextMenu.component;
    const confirmDelete = window.confirm(
      `Delete ${comp.partNumber}?\n\nThis will remove this catalog component from your library. This cannot be undone.`
    );

    if (confirmDelete) {
      try {
        const { deleteCatalogComponent } = useStore.getState();
        await deleteCatalogComponent(comp.id);
        handleCloseContextMenu();
      } catch (error) {
        alert(`Failed to delete component: ${error.message}`);
      }
    } else {
      handleCloseContextMenu();
    }
  };

  const handleToggleFavorite = async (event, comp) => {
    event.stopPropagation(); // Prevent adding component to canvas
    try {
      await toggleFavorite(comp.id, !comp.isFavorite);
    } catch (error) {
      alert(`Failed to toggle favorite: ${error.message}`);
    }
  };

  const handleDuplicateCatalogComponent = () => {
    if (!contextMenu?.component) return;

    const comp = contextMenu.component;
    const sourceComp = componentLibrary[comp.type];

    // Open modal pre-filled with source component data
    setSelectedCategory(sourceComp.category);
    setDuplicateSource({
      baseType: sourceComp.baseType,
      manufacturer: sourceComp.manufacturer,
      partNumber: '', // User must change
      parameters: { ...sourceComp.parameters }
    });
    setIsCatalogModalOpen(true);
    handleCloseContextMenu();
  };

  const handleEditCatalogComponent = () => {
    if (!contextMenu?.component) return;

    const comp = contextMenu.component;
    const sourceComp = componentLibrary[comp.type];

    setEditingComponent({
      id: sourceComp.id,
      type: comp.type,
      baseType: sourceComp.baseType,
      partNumber: sourceComp.partNumber,
      manufacturer: sourceComp.manufacturer,
      parameters: { ...sourceComp.parameters }
    });
    setIsCatalogModalOpen(true);
    handleCloseContextMenu();
  };

  const handleUploadS2P = () => {
    if (!contextMenu?.component) return;

    setS2pUploadComponent(contextMenu.component);
    setS2pUploadModalOpen(true);
    handleCloseContextMenu();
  };

  const handleS2PUploadSuccess = async (result) => {
    console.log('S2P upload successful:', result);
    // Reload component library to refresh hasS2PData flags
    await loadComponentLibrary();
  };

  const filteredCategories = Object.entries(COMPONENT_CATEGORIES).map(([key, category]) => {
    // Group components by subcategory within this category
    const subcategoryGroups = {};

    // Process generic components
    category.components.forEach(compType => {
      const comp = COMPONENT_LIBRARY[compType];
      const searchName = comp.category === 'amplifiers' ? `Generic ${comp.displayName}` : comp.displayName;
      const matchesSearch = comp && searchName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = componentFilter === 'all' || componentFilter === 'generic';

      if (matchesSearch && matchesFilter) {
        // Use the component type as the subcategory for generic components
        if (!subcategoryGroups[compType]) {
          subcategoryGroups[compType] = {
            name: comp.displayName,
            generic: [],
            catalog: []
          };
        }
        subcategoryGroups[compType].generic.push(compType);
      }
    });

    // Process catalog components
    catalogComponents
      .filter(comp => {
        const matchesCategory = comp.category === key;
        const matchesSearch = searchTerm === '' ||
          comp.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          comp.partNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          comp.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = componentFilter === 'all' || componentFilter === 'catalog';
        return matchesCategory && matchesSearch && matchesFilter;
      })
      .forEach(comp => {
        const subcategory = comp.subcategory || comp.baseType || 'generic';

        // Ensure subcategory group exists
        if (!subcategoryGroups[subcategory]) {
          subcategoryGroups[subcategory] = {
            name: COMPONENT_LIBRARY[subcategory]?.displayName || subcategory,
            generic: [],
            catalog: []
          };
        }
        subcategoryGroups[subcategory].catalog.push(comp.type);
      });

    // Filter out empty subcategory groups
    const nonEmptyGroups = Object.entries(subcategoryGroups)
      .filter(([_, group]) => group.generic.length > 0 || group.catalog.length > 0)
      .reduce((acc, [subcatKey, group]) => {
        acc[subcatKey] = group;
        return acc;
      }, {});

    return {
      key,
      name: category.name,
      subcategoryGroups: nonEmptyGroups
    };
  }).filter(cat => Object.keys(cat.subcategoryGroups).length > 0);

  // Collect all filtered components for Enter key functionality
  const allFilteredComponents = [];
  filteredCategories.forEach(cat => {
    Object.values(cat.subcategoryGroups).forEach(group => {
      group.generic.forEach(compType => allFilteredComponents.push({ type: 'generic', compType }));
      group.catalog.forEach(compType => allFilteredComponents.push({ type: 'catalog', compType }));
    });
  });

  // Handle Enter key to add single filtered component
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && allFilteredComponents.length === 1) {
      const comp = allFilteredComponents[0];
      handleAddComponent(comp.compType);
      setSearchTerm('');
    }
  };

  // Disable component selector during tutorial steps 0-2 (welcome, pause-intro, palette)
  const isPanelDisabled = showTutorial && tutorialStep < 3;

  return (
    <Container id="left-panel-container" $disabled={isPanelDisabled}>
      {isPanelDisabled && <DisabledOverlay />}
      <TitleBar>
        <Title>Components</Title>
        <ChainModeToggle
          id="chain-mode-toggle"
          onClick={handleChainModeToggle}
          $active={chainMode}
          title={chainMode ? "Chain Mode: ON (C)\nAuto-connect and position" : "Chain Mode: OFF (C)\nManual placement"}
        >
          🔗
        </ChainModeToggle>
      </TitleBar>

      <FilterSelect value={componentFilter} onChange={(e) => setComponentFilter(e.target.value)}>
        <option value="all">All Components</option>
        <option value="generic">Generic Only</option>
        <option value="catalog">Catalog Only</option>
      </FilterSelect>

      <SearchInput
        type="text"
        placeholder="Search components..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleSearchKeyDown}
      />

      <CategoryList ref={categoryListRef}>
        {/* Favorites Section */}
        {(() => {
          const favorites = Object.values(componentLibrary).filter(comp => comp.isCatalog && comp.isFavorite);
          if (favorites.length === 0) return null;

          return (
            <CategorySection expanded={showFavorites}>
              <CategoryHeader
                expanded={showFavorites}
                onClick={() => setShowFavorites(!showFavorites)}
              >
                <Arrow expanded={showFavorites}>▶</Arrow>
                <CategoryName>⭐ Favorites ({favorites.length})</CategoryName>
              </CategoryHeader>

              {showFavorites && (
                <ComponentList>
                  {favorites.map(comp => (
                    <CatalogCard
                      key={comp.type}
                      draggable
                      onDragStart={(e) => handleDragStart(e, comp.type)}
                      onClick={() => handleAddComponent(comp.type)}
                      onContextMenu={(e) => handleContextMenu(e, comp)}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({ comp, x: rect.right + 10, y: rect.top });
                        // Track for tutorial
                        if (showTutorial && tutorialStep === 14) {
                          completeTutorialAction('catalogHovered');
                        }
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      {comp.baseType && COMPONENT_SYMBOLS[comp.baseType] && (
                        <ComponentSymbol viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                          <path d={COMPONENT_SYMBOLS[comp.baseType]} />
                        </ComponentSymbol>
                      )}
                      <CatalogInfo>
                        <CatalogPartNumber>{comp.partNumber}</CatalogPartNumber>
                        <CatalogComponentType>{COMPONENT_LIBRARY[comp.baseType]?.displayName || comp.baseType}</CatalogComponentType>
                      </CatalogInfo>
                      <DataBadgesContainer>
                        {comp.hasS2P && (
                          <S2PBadge title="S2P file available">📊</S2PBadge>
                        )}
                        {comp.hasFreqData && (
                          <FreqDataBadge title="Frequency database available">📈</FreqDataBadge>
                        )}
                        {comp.isFavorite && (
                          <FavoriteIndicator title="Favorited">★</FavoriteIndicator>
                        )}
                      </DataBadgesContainer>
                    </CatalogCard>
                  ))}
                </ComponentList>
              )}
            </CategorySection>
          );
        })()}

        {filteredCategories.map(category => {
          const isExpanded = expandedCategories.has(category.key);
          return (
            <CategorySection key={category.key} expanded={isExpanded}>
              <CategoryHeader
                expanded={isExpanded}
                onClick={() => {
                  setExpandedCategories(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(category.key)) {
                      newSet.delete(category.key);
                    } else {
                      newSet.add(category.key);
                    }
                    return newSet;
                  });
                }}
              >
                <Arrow expanded={isExpanded}>▶</Arrow>
                <CategoryName>{category.name}</CategoryName>
              </CategoryHeader>

              {isExpanded && (
                <>
                  {/* Render subcategory groups */}
                  {Object.entries(category.subcategoryGroups).map(([subcatKey, group]) => (
                    <div key={subcatKey}>
                      {/* Generic component for this subcategory */}
                      {group.generic.length > 0 && (
                        <ComponentList>
                          {group.generic.map(compType => {
                            const comp = COMPONENT_LIBRARY[compType];
                            return (
                              <ComponentCard
                                key={compType}
                                data-component-type={compType}
                                draggable
                                onDragStart={(e) => handleDragStart(e, compType)}
                                onClick={() => handleAddComponent(compType)}
                              >
                                {COMPONENT_SYMBOLS[compType] && (
                                  <ComponentSymbol viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d={COMPONENT_SYMBOLS[compType]} />
                                  </ComponentSymbol>
                                )}
                                <ComponentName>{comp.category === 'amplifiers' ? `Generic ${comp.displayName}` : comp.displayName}</ComponentName>
                              </ComponentCard>
                            );
                          })}
                        </ComponentList>
                      )}

                      {/* Catalog components for this subcategory */}
                      {group.catalog.length > 0 && (
                        <ComponentList>
                          {group.catalog.map(compType => {
                            const comp = componentLibrary[compType];
                            if (!comp) return null;
                            return (
                              <CatalogCard
                                key={compType}
                                draggable
                                onDragStart={(e) => handleDragStart(e, compType)}
                                onClick={() => handleAddComponent(compType)}
                                onContextMenu={(e) => handleContextMenu(e, comp)}
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltip({ comp, x: rect.right + 10, y: rect.top });
                                  // Track for tutorial
                                  if (showTutorial && tutorialStep === 14) {
                                    completeTutorialAction('catalogHovered');
                                  }
                                }}
                                onMouseLeave={() => setTooltip(null)}
                              >
                                {comp.baseType && COMPONENT_SYMBOLS[comp.baseType] && (
                                  <ComponentSymbol viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d={COMPONENT_SYMBOLS[comp.baseType]} />
                                  </ComponentSymbol>
                                )}
                                <CatalogInfo>
                                  <CatalogPartNumber>{comp.partNumber}</CatalogPartNumber>
                                  <CatalogComponentType>{COMPONENT_LIBRARY[comp.baseType]?.displayName || comp.baseType}</CatalogComponentType>
                                </CatalogInfo>
                                <DataBadgesContainer>
                                  {comp.hasS2PData && (
                                    <S2PBadge title="S2P file available">📊</S2PBadge>
                                  )}
                                  {comp.hasFreqData && (
                                    <FreqDataBadge title="Frequency database available">📈</FreqDataBadge>
                                  )}
                                  {comp.isFavorite && (
                                    <FavoriteIndicator title="Favorited">★</FavoriteIndicator>
                                  )}
                                </DataBadgesContainer>
                              </CatalogCard>
                            );
                          })}
                        </ComponentList>
                      )}
                    </div>
                  ))}

                  {/* Add Catalog Component Button - hide for sources, measurement, system */}
                  {!['sources', 'measurement', 'system'].includes(category.key) && (
                    <AddButton onClick={() => openCatalogModal(category.key)}>
                      + Add Catalog Component
                    </AddButton>
                  )}
                </>
              )}
            </CategorySection>
          );
        })}
      </CategoryList>

      <CatalogComponentModal
        isOpen={isCatalogModalOpen}
        onClose={() => {
          setIsCatalogModalOpen(false);
          setDuplicateSource(null);
          setEditingComponent(null);
        }}
        baseCategory={selectedCategory}
        duplicateSource={duplicateSource}
        editingComponent={editingComponent}
      />

      {/* Context Menu for Catalog Component */}
      {contextMenu && (
        <ContextMenu
          style={{
            top: contextMenu.mouseY,
            left: contextMenu.mouseX
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ContextMenuItem onClick={() => {
            handleToggleFavorite({ stopPropagation: () => {} }, contextMenu.component);
            handleCloseContextMenu();
          }}>
            {contextMenu.component.isFavorite ? '★ Remove from Favorites' : '☆ Add to Favorites'}
          </ContextMenuItem>
          <ContextMenuItem onClick={handleUploadS2P}>
            📊 Upload S2P File
          </ContextMenuItem>
          <ContextMenuItem onClick={handleDuplicateCatalogComponent}>
            📋 Duplicate {contextMenu.component.partNumber}
          </ContextMenuItem>
          <ContextMenuItem onClick={handleEditCatalogComponent}>
            ✏️ Edit {contextMenu.component.partNumber}
          </ContextMenuItem>
          {contextMenu.component.manufacturer === 'Guerrilla RF' && (
            <ContextMenuItem onClick={handleViewDatasheet}>
              📄 View Data Sheet
            </ContextMenuItem>
          )}
          <ContextMenuItem onClick={handleDeleteCatalogComponent}>
            🗑️ Delete {contextMenu.component.partNumber}
          </ContextMenuItem>
        </ContextMenu>
      )}

      {/* Tooltip for Component Specs */}
      {tooltip && (
        <Tooltip style={{ top: tooltip.y, left: tooltip.x }}>
          <TooltipTitle>{tooltip.comp.partNumber}</TooltipTitle>
          {Object.entries(tooltip.comp.parameters)
            .filter(([key, param]) => {
              const whitelist = ['gain', 'nf', 'noiseFigure', 'p1db', 'oip3', 'supplyVoltage', 'idleCurrent', 'leakageCurrent', 'frequencyMin', 'frequencyMax', 'package', 'technology'];
              return whitelist.includes(key) && param.value !== null && param.value !== undefined && param.value !== '';
            })
            .map(([key, param]) => (
            <TooltipRow key={key}>
              {param.name === 'P1dB' ? 'OP1dB' : param.name}: {param.value} {param.unit}
            </TooltipRow>
          ))}
        </Tooltip>
      )}

      {/* S2P Upload Modal */}
      <S2PUploadModal
        isOpen={s2pUploadModalOpen}
        onClose={() => setS2pUploadModalOpen(false)}
        componentLibraryId={s2pUploadComponent?.id}
        componentName={s2pUploadComponent?.partNumber}
        onUploadSuccess={handleS2PUploadSuccess}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 250px;
  background: ${THEME.sidebarBackground};
  border-right: 1px solid #404040;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: opacity 0.2s ease;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  cursor: not-allowed;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.lg};
  border-bottom: 1px solid #404040;
`;

const Title = styled.h2`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xl};
  margin: 0;
`;

const ChainModeToggle = styled.button`
  background: ${props => props.$active ? 'rgba(200, 60, 60, 0.15)' : 'transparent'};
  border: ${props => props.$active ? '1px solid #c83c3c' : '1px solid transparent'};
  font-size: 20px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  transition: all 0.2s ease;
  filter: ${props => props.$active
    ? 'drop-shadow(0 0 4px #c83c3c) brightness(1.1) saturate(1.3)'
    : 'grayscale(100%) brightness(0.5)'};

  &:hover {
    background: ${props => props.$active ? 'rgba(200, 60, 60, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
    filter: ${props => props.$active
      ? 'drop-shadow(0 0 6px #c83c3c) brightness(1.15) saturate(1.3)'
      : 'grayscale(100%) brightness(0.7)'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SearchInput = styled.input`
  background: ${THEME.inputBackground};
  color: ${THEME.textPrimary};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm};
  margin: ${THEME.spacing.md};
  font-size: ${THEME.fontSize.sm};

  &:focus {
    border-color: ${THEME.accentPrimary};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const CategoryList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const CategorySection = styled.div`
  border-bottom: 1px solid #404040;
  background: ${props => props.expanded ? 'rgba(220, 38, 38, 0.08)' : 'transparent'};
  border-left: ${props => props.expanded ? `3px solid ${THEME.accentPrimary}` : '3px solid transparent'};
  transition: all ${THEME.transitionFast};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: ${THEME.spacing.md};
  cursor: pointer;
  transition: background ${THEME.transitionFast};
  background: ${props => props.expanded ? 'rgba(220, 38, 38, 0.12)' : 'transparent'};

  &:hover {
    background: ${props => props.expanded ? 'rgba(220, 38, 38, 0.18)' : THEME.panelBackground};
  }
`;

const CategoryName = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  font-weight: 600;
`;

const Arrow = styled.span`
  color: ${props => props.expanded ? THEME.accentPrimary : THEME.textSecondary};
  font-size: 14px;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0)'};
  transition: all ${THEME.transitionFast};
  margin-right: ${THEME.spacing.sm};
`;

const ComponentList = styled.div`
  padding: ${THEME.spacing.sm} ${THEME.spacing.sm} ${THEME.spacing.sm} 20px;
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.sm};
  margin-left: ${THEME.spacing.md};
  border-left: 2px solid ${THEME.accentPrimary};
  position: relative;
`;

const ComponentCard = styled.div`
  background: ${THEME.cardBackground};
  border: 1px solid ${THEME.cardBorder};
  border-left: 3px solid rgba(220, 38, 38, 0.3);
  border-radius: ${THEME.borderRadiusSmall};
  padding: 6px 12px;
  cursor: grab;
  transition: all ${THEME.transitionFast};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${THEME.panelBackground};
    border-color: ${THEME.accentPrimary};
    border-left-color: ${THEME.accentPrimary};
    transform: translateX(4px);
  }

  &:active {
    cursor: grabbing;
  }
`;

const ComponentSymbol = styled.svg`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  opacity: 0.7;

  path {
    fill: none;
    stroke: ${THEME.textPrimary};
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const ComponentName = styled.div`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.sm};
  font-weight: 500;
  flex: 1;
`;

const FilterSelect = styled.select`
  margin: 0 ${THEME.spacing.md} ${THEME.spacing.sm};
  padding: 8px 12px;
  background: ${THEME.backgroundDark};
  border: 1px solid ${THEME.borderColor};
  border-radius: 6px;
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${THEME.accentBlue};
  }

  option {
    background: ${THEME.backgroundDark};
    color: ${THEME.textPrimary};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${THEME.borderColor};
  margin: ${THEME.spacing.sm} ${THEME.spacing.md};
`;

const SubcategoryHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${THEME.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  margin-top: ${THEME.spacing.sm};
  border-bottom: 1px solid ${THEME.borderColor};
`;

const CatalogCard = styled(ComponentCard)`
  height: auto;
  min-height: 50px;
  padding: 8px 10px;
  border-left: 3px solid ${THEME.accentBlue};
  position: relative;

  /* Create a stacking context so star position is relative to card's untransformed position */
  &:hover {
    z-index: 1;
  }
`;

const CatalogInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
`;

const CatalogPartNumber = styled.div`
  color: #ff6b6b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
`;

const CatalogComponentType = styled.div`
  color: ${THEME.textPrimary};
  font-size: 11px;
  line-height: 1.2;
`;

const AddButton = styled.button`
  margin: ${THEME.spacing.sm} ${THEME.spacing.md};
  padding: 8px 12px;
  background: ${THEME.backgroundLight};
  border: 1px dashed ${THEME.borderColor};
  border-radius: 6px;
  color: ${THEME.accentBlue};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;
  transition: all 150ms ease;

  &:hover {
    background: ${THEME.hoverBackground};
    border-color: ${THEME.accentBlue};
  }
`;

const ContextMenu = styled.div`
  position: fixed;
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.borderColor};
  border-radius: 6px;
  box-shadow: ${THEME.shadowLarge};
  padding: 4px 0;
  min-width: 180px;
  z-index: 10000;
`;

const ContextMenuItem = styled.div`
  padding: 10px 16px;
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: ${THEME.hoverBackground};
  }
`;

const DataBadgesContainer = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 2px;
  pointer-events: none;
  z-index: 10;
  user-select: none;
  transition: transform 150ms ease;

  /* Counteract parent card's hover transform */
  *:hover > & {
    transform: translateX(-4px);
  }
`;

const S2PBadge = styled.span`
  font-size: 12px;
  background: rgba(59, 130, 246, 0.2);
  padding: 2px 4px;
  border-radius: 3px;
  cursor: help;
  border: 1px solid rgba(59, 130, 246, 0.4);
`;

const FreqDataBadge = styled.span`
  font-size: 12px;
  background: rgba(76, 175, 80, 0.2);
  padding: 2px 4px;
  border-radius: 3px;
  cursor: help;
  border: 1px solid rgba(76, 175, 80, 0.4);
`;

const FavoriteIndicator = styled.div`
  font-size: 14px;
  color: #FFD700;
  pointer-events: none;
  user-select: none;
`;

const Tooltip = styled.div`
  position: fixed;
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.borderColor};
  border-radius: 6px;
  padding: 8px 12px;
  z-index: 10001;
  pointer-events: none;
  box-shadow: ${THEME.shadowLarge};
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
  color: ${THEME.textPrimary};
`;

const TooltipRow = styled.div`
  font-size: ${THEME.fontSize.xs};
  color: ${THEME.textSecondary};
`;

export default LeftPanel;
